import { useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Format price with proper currency display for Google Play compliance
const formatPrice = (price, currency = "USD") => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  } catch {
    return `$${price} USD`;
  }
};

// Subscription price constant
const SUBSCRIPTION_PRICE = 19;
const SUBSCRIPTION_CURRENCY = "USD";

function SubscriptionExpired({ config, onLogout, subscriptionStatus, user, onShowPrivacy }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubscribe = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.location.href = response.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Error creating checkout session");
      setLoading(false);
    }
  };

  const isTrialExpired = subscriptionStatus?.trial_expired || 
    (subscriptionStatus?.subscription_status === "trial" && subscriptionStatus?.trial_days_remaining <= 0);

  // Formatted price for consistent display
  const formattedPrice = formatPrice(config?.SUBSCRIPTION_PRICE || SUBSCRIPTION_PRICE, SUBSCRIPTION_CURRENCY);

  return (
    <div style={{ 
      minHeight: "100vh", 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center",
      padding: "20px"
    }}>
      <div className="card" style={{ 
        maxWidth: "550px", 
        textAlign: "center",
        background: "linear-gradient(135deg, #1a1a1a 0%, #2a1a1a 100%)",
        border: isTrialExpired ? "2px solid #FFD700" : "2px solid #ff6b6b"
      }}>
        <div style={{ fontSize: "4rem", marginBottom: "20px" }}>
          {isTrialExpired ? "⏰" : "⚠️"}
        </div>
        
        <h2 style={{ color: isTrialExpired ? "#FFD700" : "#ff6b6b", marginBottom: "15px" }}>
          {isTrialExpired ? "7-Day Free Trial Ended" : "Subscription Expired"}
        </h2>
        
        {user?.username && (
          <p style={{ color: "#87CEEB", marginBottom: "15px" }}>
            Welcome back, <strong>{user.username}</strong>
          </p>
        )}
        
        <p style={{ color: "#aaa", marginBottom: "25px", fontSize: "1.05rem", lineHeight: "1.6" }}>
          {isTrialExpired ? (
            <>
              Your 7-day free trial has ended. Subscribe now to continue your 
              learning journey at the Yisrael Learning Center.
            </>
          ) : (
            <>
              Your subscription has expired or is inactive. Please renew to continue 
              accessing the curriculum, quizzes, and certificates.
            </>
          )}
        </p>

        {error && <div className="alert alert-error" style={{ marginBottom: "20px" }}>{error}</div>}

        {/* Trial Info Box */}
        {isTrialExpired && (
          <div style={{ 
            background: "#1a2a1a", 
            padding: "15px", 
            borderRadius: "8px",
            marginBottom: "20px",
            borderLeft: "4px solid #90EE90"
          }}>
            <p style={{ color: "#90EE90", margin: 0, fontSize: "0.95rem" }}>
              ✓ Your progress and certificates are saved!
              <br />
              <span style={{ color: "#aaa" }}>Subscribe to pick up right where you left off.</span>
            </p>
          </div>
        )}

        <div style={{ 
          background: "#0a0a0a", 
          padding: "25px", 
          borderRadius: "12px",
          marginBottom: "25px",
          border: "1px solid #333"
        }}>
          <h3 style={{ color: "#FFD700", margin: "0 0 15px 0" }}>
            {isTrialExpired ? "Start Your Subscription" : "Renew Your Access"}
          </h3>
          <div style={{ fontSize: "3rem", fontWeight: "bold", color: "#FFD700" }}>
            {formattedPrice}
            <span style={{ fontSize: "1rem", color: "#aaa" }}>/month</span>
          </div>
          <p style={{ color: "#666", fontSize: "0.8rem", margin: "5px 0 0" }}>
            Billed monthly in {SUBSCRIPTION_CURRENCY}
          </p>
          <div style={{ marginTop: "15px", display: "flex", flexDirection: "column", gap: "8px", textAlign: "left", padding: "0 20px" }}>
            <div style={{ color: "#aaa", fontSize: "0.9rem" }}>
              ✓ Unlimited access to all K-12 courses
            </div>
            <div style={{ color: "#aaa", fontSize: "0.9rem" }}>
              ✓ Quizzes and chapter exams
            </div>
            <div style={{ color: "#aaa", fontSize: "0.9rem" }}>
              ✓ Ordination program access
            </div>
            <div style={{ color: "#aaa", fontSize: "0.9rem" }}>
              ✓ Printable certificates
            </div>
            <div style={{ color: "#aaa", fontSize: "0.9rem" }}>
              ✓ Cancel anytime
            </div>
          </div>
        </div>

        <button
          onClick={handleSubscribe}
          disabled={loading}
          data-testid="subscribe-btn"
          style={{
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            color: "#000",
            fontWeight: "bold",
            padding: "18px 40px",
            fontSize: "1.1rem",
            width: "100%",
            marginBottom: "15px",
            border: "none",
            borderRadius: "8px",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? "Processing..." : isTrialExpired ? `Subscribe Now - ${formattedPrice}/month` : "Renew Subscription Now"}
        </button>

        <button
          onClick={onLogout}
          data-testid="logout-expired-btn"
          style={{
            background: "transparent",
            border: "1px solid #666",
            color: "#aaa",
            width: "100%",
            padding: "12px"
          }}
        >
          Logout
        </button>

        <p style={{ 
          marginTop: "20px", 
          fontSize: "0.85rem", 
          color: "#999" 
        }}>
          Questions? Contact {config?.SUPPORT_EMAIL || "Yisraellearningcenter@gmail.com"}
        </p>

        {/* Payment disclaimer for Google Play compliance */}
        <p style={{ 
          marginTop: "15px", 
          fontSize: "0.75rem", 
          color: "#666",
          borderTop: "1px solid #333",
          paddingTop: "15px"
        }}>
          Payment processed securely via Stripe. Price shown in {SUBSCRIPTION_CURRENCY}. 
          Subscription renews monthly until cancelled.
        </p>
      </div>
    </div>
  );
}

export default SubscriptionExpired;
